import { useEffect } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";

import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Button from "../../components/ui/button/Button";
import ImageInput from "../../components/form/input/ImageInput";
import MenuItemLangForm from "./MenuItemLangForm";
import MenuItemOptions from "../../components/common/MenuItem/MenuItemOptions";
import MultiSelect from "../../components/form/MultiSelect";
import useMenuSections from "../../hooks/MenuSection/useMenuSections";
import Spinner from "../../components/ui/spinner";
import NutrientsInput from "./NutrientsInput";
import PriceAmountInput from "./PriceAmountInput";

import type {
  MenuItemVariables,
  MenuItemFormProps,
  MenuItemLang,
} from "../../types/menuItemTypes";

const MenuItemForm: React.FC<MenuItemFormProps> = ({
  onSubmit,
  menuItem,
  menu,
}) => {
  const { menuSections, loading, refetchMenuSections } = useMenuSections(
    menu.id,
    {
      fetchPolicy: "network-only",
      refetchOnMount: false,
    }
  );

  const methods = useForm<MenuItemVariables>({
    defaultValues: {
      menuId: menu.id,
      menuSectionIds: menuItem?.menuSectionIds ?? [],
    },
    mode: "onChange",
  });

  const {
    formState: { errors },
    control,
    register,
    reset,
    handleSubmit,
  } = methods;

  const multiOptions = () => {
    if (!menuSections) return [];

    return menuSections.map((ms) => ({
      value: String(ms.id),
      text: ms.name,
    }));
  };

  const menuItemLangs = (): MenuItemLang[] => {
    if (menuItem) {
      return menuItem.menuItemLangs;
    }

    return (
      menu?.languages.map((l) => ({
        name: "",
        description: "",
        language: l.language,
      })) ?? []
    );
  };

  useEffect(() => {
    if (menuItem) {
      reset({
        ...menuItem,
        menuId: menu.id,
        menuSectionIds: menuItem.menuSectionIds ?? [],
      });
    } else {
      reset({
        menuId: menu.id,
        menuSectionIds: [],
      });
    }
  }, [menuItem, menu.id, reset]);

  useEffect(() => {
    refetchMenuSections();
  }, [refetchMenuSections]);

  if (loading) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ComponentCard
            title={`${menuItem?.id ? "Edit" : "New"} menu item`}
          >
            <div>
              <Label htmlFor="name">Languages</Label>

              <MenuItemLangForm
                id="menuItemLangs"
                menuItemLangs={menuItemLangs()}
                control={control}
                register={register}
              />
            </div>

            <div>
              <Controller
                name="menuSectionIds"
                control={control}
                rules={{
                  required: "Select at least one menu section",
                  validate: (value) =>
                    (value?.length ?? 0) > 0 ||
                    "Select at least one menu section",
                }}
                render={({ field, fieldState }) => (
                  <MultiSelect
                    id="menuSectionIds"
                    label="Menu Sections"
                    options={multiOptions()}
                    value={(field.value ?? []).map(String)}
                    onChange={(values) => {
                      field.onChange(values);
                    }}
                    onBlur={field.onBlur}
                    required
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <div>
              <Label>Image</Label>

              <ImageInput
                className="mt-2"
                register={register}
                name="image"
                imageUrl={menuItem?.img.medium}
                error={errors?.image}
              />
            </div>

            <div>
              <PriceAmountInput
                menuItem={menuItem}
                control={control}
                register={register}
                currencyCode={menu.currency.code}
              />
            </div>

            <div>
              <Label>Nutrients</Label>

              <NutrientsInput
                menuItem={menuItem}
                control={control}
                register={register}
              />
            </div>

            <div>
              <Label>Options</Label>
              <MenuItemOptions menuItem={menuItem} />
            </div>

            <Button size="md" variant="primary">
              {menuItem ? "Update" : "Save"}
            </Button>
          </ComponentCard>
        </form>
      </FormProvider>
    </div>
  );
};

export default MenuItemForm;